import { osnovaApi } from '../base';
import type {
  ContractHandoff,
  HandoffResult,
  OrgService,
  OrgServiceBody,
  OrgServicesResponse,
  PlatformNotification,
} from '@entities/notification/types';

export const notificationsApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /** Личная лента уведомлений. Доступна любому сотруднику — права на агентов
     *  тут ни при чём: бухгалтер узнаёт о договоре именно отсюда. */
    listNotifications: b.query<PlatformNotification[], void>({
      query: () => '/notifications',
      transformResponse: (res: { items: PlatformNotification[] }) => res.items,
      providesTags: ['PlatformNotification'],
    }),
    markNotificationRead: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/notifications/${id}/read`, method: 'POST' }),
      invalidatesTags: ['PlatformNotification'],
    }),
    markAllNotificationsRead: b.mutation<{ ok: boolean }, void>({
      query: () => ({ url: '/notifications/read-all', method: 'POST' }),
      invalidatesTags: ['PlatformNotification'],
    }),

    // ===== Службы компании (администратор) =====

    /** Службы вместе с составом и списком видов обязательств. */
    listOrgServices: b.query<OrgServicesResponse, void>({
      query: () => '/org-services',
      providesTags: ['OrgService'],
    }),
    createOrgService: b.mutation<OrgService, OrgServiceBody>({
      query: (body) => ({ url: '/org-services', method: 'POST', body }),
      invalidatesTags: ['OrgService'],
    }),
    /** Правка службы. `version` из чтения — иначе 409, чтобы не затереть чужое. */
    updateOrgService: b.mutation<OrgService, { id: string; body: OrgServiceBody }>({
      query: ({ id, body }) => ({ url: `/org-services/${id}`, method: 'PUT', body }),
      invalidatesTags: ['OrgService'],
    }),
    deleteOrgService: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/org-services/${id}`, method: 'DELETE' }),
      invalidatesTags: ['OrgService'],
    }),
    addOrgServiceMember: b.mutation<{ ok: boolean }, { id: string; user_id: string }>({
      query: ({ id, user_id }) => ({
        url: `/org-services/${id}/members`,
        method: 'POST',
        body: { user_id },
      }),
      invalidatesTags: ['OrgService'],
    }),
    removeOrgServiceMember: b.mutation<{ ok: boolean }, { id: string; user_id: string }>({
      query: ({ id, user_id }) => ({
        url: `/org-services/${id}/members/${user_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['OrgService'],
    }),

    // ===== Ввод договора в работу (Юрист) =====

    /** Кому передан договор и кто принял его в работу. */
    listContractHandoffs: b.query<ContractHandoff[], string>({
      query: (contractId) => `/agents/statut/contracts/${contractId}/handoffs`,
      transformResponse: (res: { items: ContractHandoff[] }) => res.items,
      providesTags: (_r, _e, id) => [{ type: 'ContractHandoff', id }],
    }),
    /** Разложить обязательства по службам и уведомить их. Заодно ставит
     *  договору статус «Подписан» — раньше его не выставлял никто. */
    handOffContract: b.mutation<HandoffResult, string>({
      query: (contractId) => ({
        url: `/agents/statut/contracts/${contractId}/handoff`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'ContractHandoff', id },
        'Contract',
        'PlatformNotification',
      ],
    }),
    /** «Принял в работу» от участника службы — из карточки договора или прямо
     *  из колокольчика: у бухгалтера и механика доступа к «Юристу» нет. */
    ackContractHandoff: b.mutation<
      { ok: boolean },
      { id: string; contractId: string; note?: string }
    >({
      query: ({ id, note }) => ({
        url: `/agents/statut/handoffs/${id}/ack`,
        method: 'POST',
        body: { note: note ?? null },
      }),
      invalidatesTags: (_r, _e, { contractId }) => [
        { type: 'ContractHandoff', id: contractId },
        'PlatformNotification',
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useListOrgServicesQuery,
  useCreateOrgServiceMutation,
  useUpdateOrgServiceMutation,
  useDeleteOrgServiceMutation,
  useAddOrgServiceMemberMutation,
  useRemoveOrgServiceMemberMutation,
  useListContractHandoffsQuery,
  useHandOffContractMutation,
  useAckContractHandoffMutation,
} = notificationsApi;
